import React, { useState, useRef, useEffect } from 'react';
import MainLayout from '../../components/MainLayout';
import { sendAgentMessageStream } from '../../services/api';
import DynamicChart from '../../components/DynamicChart';
import TableRenderer from '../../components/TableRenderer';
import { getStoredUser } from '../../services/authService';
import { useTranslation } from '../../context/LanguageContext';
import './AIAnalysis.css';

function AIAnalysis() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState(() => ([
    {
      role: 'assistant',
      content: t('pages.ai.aiAnalysis.welcome'),
      timestamp: new Date()
    }
  ]));
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingText, setStreamingText] = useState('');
  const [statusText, setStatusText] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // 메시지가 추가될 때마다 스크롤을 아래로
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // textarea 자동 높이 조절
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [inputValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputValue;
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setStreamingText('');
    setStatusText('');

    try {
      // 멀티 에이전트 스트리밍 API 호출
      const welcomeMatch = t('pages.ai.aiAnalysis.welcomeMatch');
      const conversationHistory = messages.filter(msg => msg.role === 'user' ||
        (msg.role === 'assistant' && !msg.content.includes(welcomeMatch)));

      await sendAgentMessageStream(
        messageToSend,
        conversationHistory,
        // onToken: 토큰이 올 때마다 호출
        (token, fullText) => {
          setStreamingText(fullText);
        },
        // onStatus: 상태 업데이트
        (status) => {
          setStatusText(status);
        },
        // onComplete: 완료 시 호출
        (response) => {
          const assistantMessage = {
            role: 'assistant',
            content: response.natural_language || t('pages.ai.aiAnalysis.noResponse'),
            blocks: response.blocks || [],
            intent: response.intent,
            sqlQuery: response.sql_query,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);
          setStreamingText('');
          setStatusText('');
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('AI 메시지 처리 실패:', error);

      const errorMessage = {
        role: 'assistant',
        content: t('pages.ai.aiAnalysis.errorMessage'),
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(error.message);
      setStreamingText('');
      setStatusText('');
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 부서별 추천 질문 가져오기
  const getSuggestedQuestions = () => {
    const user = getStoredUser();
    const department = user?.department || '';

    // 품질관리팀 추천 질문
    if (department.includes('품질') || department.includes('QC') || department.includes('QA')) {
      return [
        t('pages.ai.aiAnalysis.suggestions.qualityDefectTrend'),
        t('pages.ai.aiAnalysis.suggestions.qualityTopAlarmEquip'),
        t('pages.ai.aiAnalysis.suggestions.qualityTempAnalysis'),
        t('pages.ai.aiAnalysis.suggestions.qualityDefectByEquip')
      ];
    }

    // 생산관리팀 추천 질문
    if (department.includes('생산') || department.includes('제조')) {
      return [
        t('pages.ai.aiAnalysis.suggestions.productionRunningEquip'),
        t('pages.ai.aiAnalysis.suggestions.productionTopOutput'),
        t('pages.ai.aiAnalysis.suggestions.productionTrend'),
        t('pages.ai.aiAnalysis.suggestions.productionUtilizationCompare')
      ];
    }

    // 설비/유지보수팀 추천 질문
    if (department.includes('설비') || department.includes('유지보수') || department.includes('보전')) {
      return [
        t('pages.ai.aiAnalysis.suggestions.maintenanceTopAlarm'),
        t('pages.ai.aiAnalysis.suggestions.maintenanceAlarmTrend'),
        t('pages.ai.aiAnalysis.suggestions.maintenanceCycleTimeAnalysis'),
        t('pages.ai.aiAnalysis.suggestions.maintenanceIdleEquip')
      ];
    }

    // 기본 추천 질문 (부서 정보 없거나 기타 부서)
    return [
      t('pages.ai.aiAnalysis.suggestions.defaultRunningEquip'),
      t('pages.ai.aiAnalysis.suggestions.defaultTopAlarm'),
      t('pages.ai.aiAnalysis.suggestions.defaultTrend'),
      t('pages.ai.aiAnalysis.suggestions.defaultShotTempAnalysis')
    ];
  };

  const suggestedQuestions = getSuggestedQuestions();

  const handleSuggestionClick = (question) => {
    setInputValue(question);
    textareaRef.current?.focus();
  };

  /* 새 대화 — 환영 메시지만 남긴다 */
  const handleNewChat = () => {
    if (isLoading) return;
    setMessages([{ role: 'assistant', content: t('pages.ai.aiAnalysis.welcome'), timestamp: new Date() }]);
    setInputValue('');
    setError(null);
  };

  // ContentBlock 렌더링 함수
  const renderContentBlock = (block, index) => {
    const { type, content } = block;
    switch (type) {
      case 'table':
        return <div key={index} className="aa-block"><TableRenderer data={content} /></div>;
      case 'chart':
        return <div key={index} className="aa-block aa-chart"><DynamicChart config={content} /></div>;
      case 'text':
        return <div key={index} className="aa-text">{content}</div>;
      case 'file': {
        const kind = content.file_type === 'excel' ? 'excel' : content.file_type === 'csv' ? 'csv' : content.file_type === 'pdf' ? 'pdf' : 'file';
        const icon = { excel: 'table_chart', csv: 'csv', pdf: 'picture_as_pdf', file: 'download' }[kind];
        return (
          <div key={index} className="aa-file">
            <span className="material-symbols-outlined aa-file-icon">{icon}</span>
            <div className="aa-file-info">
              <div className="aa-file-title">
                {t(`pages.ai.aiAnalysis.file.${kind}`)}
                {content.row_count > 0 && <span className="aa-file-rows">{t('pages.ai.aiAnalysis.file.rows', { n: content.row_count })}</span>}
              </div>
              <div className="aa-file-msg">{content.message}</div>
            </div>
            {content.download_url && (
              <a href={content.download_url} download className="aa-file-btn">
                <span className="material-symbols-outlined">download</span>{t('pages.ai.aiAnalysis.file.download')}
              </a>
            )}
            {content.data_available && !content.download_url && (
              <span className="aa-file-ready"><span className="material-symbols-outlined">info</span>{t('pages.ai.aiAnalysis.file.ready')}</span>
            )}
          </div>
        );
      }
      default:
        return <div key={index} className="aa-text">{JSON.stringify(content)}</div>;
    }
  };

  /* ===== AI 분석 — 다른 화면과 같은 2단계 배경(바탕 #1A1E24 · 요소 #111418)과 헤더 막대. 스타일은 aa- 접두어 =====
   * 동작(스트리밍·추천 질문·블록 렌더링)은 그대로이고 모양만 바꿨다. 플로팅 AI 어시스턴트(ai-)와 클래스가 겹치지 않게 aa- 를 쓴다. */
  return (
    <MainLayout>
      <div className="aa-page">
        <div className="aa-header">
          <div className="aa-crumb">
            <span className="material-symbols-outlined aa-crumb-icon">text_fields_alt</span>
            <span className="aa-crumb-root">{t('sidebar.ai.title')}</span>
            <span className="material-symbols-outlined aa-crumb-sep">chevron_right</span>
            <span className="aa-crumb-page">{t('pages.ai.aiAnalysis.title')}</span>
          </div>
          <button type="button" className="aa-new" onClick={handleNewChat} disabled={isLoading || messages.length <= 1}>
            <span className="material-symbols-outlined">add_comment</span>{t('pages.ai.aiAnalysis.newChat')}
          </button>
        </div>

        <section className="aa-panel">
          <div className="aa-messages">
            <div className="aa-thread">
              {messages.map((message, index) => (
                <div key={index} className={`aa-msg aa-msg-${message.role}`}>
                  {message.role === 'assistant' && <span className="aa-avatar">AI</span>}
                  <div className="aa-msg-body">
                    <div className="aa-msg-meta">
                      <span className="aa-msg-role">{message.role === 'assistant' ? t('pages.ai.aiAnalysis.assistant') : t('pages.ai.aiAnalysis.user')}</span>
                      <span className="aa-msg-time">{formatTime(message.timestamp)}</span>
                    </div>
                    <div className={`aa-bubble ${message.isError ? 'aa-bubble-error' : ''}`}>
                      {message.content && <div className="aa-text">{message.content.replace(/<table[\s\S]*?<\/table>/gi, '').trim()}</div>}
                      {message.blocks && message.blocks.map((block, idx) => renderContentBlock(block, idx))}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="aa-msg aa-msg-assistant">
                  <span className="aa-avatar">AI</span>
                  <div className="aa-msg-body">
                    <div className="aa-msg-meta">
                      <span className="aa-msg-role">{t('pages.ai.aiAnalysis.assistant')}</span>
                      {statusText && <span className="aa-status">{statusText}</span>}
                    </div>
                    <div className="aa-bubble">
                      {streamingText
                        ? <div className="aa-text">{streamingText}<span className="aa-cursor">▌</span></div>
                        : <div className="aa-typing"><i /><i /><i /></div>}
                    </div>
                  </div>
                </div>
              )}

              {messages.length === 1 && !isLoading && (
                <div className="aa-suggest">
                  <div className="aa-suggest-title">{t('pages.ai.aiAnalysis.suggestionsTitle')}</div>
                  <div className="aa-suggest-grid">
                    {suggestedQuestions.map((question, index) => (
                      <button key={index} type="button" className="aa-suggest-item" onClick={() => handleSuggestionClick(question)}>
                        <span>{question}</span>
                        <span className="material-symbols-outlined">north_east</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="aa-input">
            <textarea
              ref={textareaRef}
              className="aa-textarea"
              placeholder={t('pages.ai.aiAnalysis.placeholder')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button type="submit" className="aa-send" disabled={!inputValue.trim() || isLoading}>
              <span className="material-symbols-outlined">send</span>{t('pages.ai.aiAnalysis.send')}
            </button>
          </form>
          {error && <div className="aa-error-note">{error}</div>}
        </section>
      </div>
    </MainLayout>
  );
}

export default AIAnalysis;
